// Redux Utils
import { useDispatch, useSelector } from "react-redux";
import {
  getUserInfo,
  getMisEmpresas,
  getMisTicketsComprados,
} from "@/features/user/userThunks";
import { getSeguidores } from "@/features/seguidores/seguidoresThunks";
import { getMyReferralLinks } from "@/features/embajador/embajadorThunks";
import { getInvitacionesDeUsuario } from "@/features/invitacion/invitacionThunks";

// React Utils
import { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";

// Skeleton
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// Assets
// import LogoPerfilEmpresa from "@/assets/avatars/avatar_amanda.webp";
// import Ambassador1 from "@/assets/recentAmbassador/mujer1.webp";
// import Ambassador2 from "@/assets/recentAmbassador/mujer2.webp";
// import Ambassador3 from "@/assets/recentAmbassador/mujer3.webp";
// import Ambassador4 from "@/assets/recentAmbassador/mujer4.webp";
// import portadaChris from "@/assets/cities/IslaDePascua.webp";
// import AmbassadorChris from "@/assets/recentAmbassador/chris.webp";
// import EventImage1 from "@/assets/eventos/ViejasDeMierda.webp";
// import EventImage2 from "@/assets/eventos/ClubAmandaDJCeleArrabal.webp";
// import EventImage3 from "@/assets/eventos/ClubAmandaFullBailableYElectronic.webp";
// import EventImage4 from "@/assets/eventos/CelebrarEnAlonso.webp";
// import EventImage5 from "@/assets/eventos/Ferxxo.webp";
// import EventImage6 from "@/assets/eventos/JorisVoorn.webp";

// Components
import LoadInfo from "@/components/LoadInfo";
import Portada from "@/components/Perfil/Portada";
import InfoPerfil from "@/components/Perfil/InfoPerfil";
import OptionsPerfil from "@/components/Perfil/OptionsPerfil";
import StatsPerfil from "@/components/Perfil/StatsPerfil";
import TicketCarousel from "@/components/TicketCarousel/TicketCarousel";
import TicketCardPerfilUsuario from "@/components/TicketCarousel/TicketCardPerfilUsuario";
import TicketCardReferralLink from "@/components/TicketCarousel/TicketCardReferralLink";
import TicketCardInvitacion from "@/components/TicketCarousel/TicketCardInvitacion";
import CardMiEmpresa from "@/components/Perfil/CardMiEmpresa";
import Tab from "@/components/Tab";
import { Link } from "react-router-dom";
import EditarInformacionPerfil from "@/components/Perfil/EditarInformacionPerfil";

import AppWhoopLogo from "@/assets/AppWhoop_logo.webp";

// Utils
import { scrollToTop } from "@/lib/utilsUI";
import { formatFechaHoraEvento } from "@/lib/utilsUI";
import { shareContent } from "@/utils/shareUtil";

export default function Perfil() {
  // Obtener la ubicación actual
  const location = useLocation();
  // Obtener el UID de los parámetros de la ruta
  const { id } = useParams();

  // ========== REFS PARA EVITAR PETICIONES DUPLICADAS ==========
  const hasLoadedBasicData = useRef(false);
  const hasLoadedTicketsData = useRef(false);
  const hasLoadedEmpresasData = useRef(false);
  const hasLoadedEventosData = useRef(false);
  const hasLoadedInvitacionesData = useRef(false);

  // Estados locales para el manejo de la carga individual por sección
  const [userInfoLoaded, setUserInfoLoaded] = useState(false);
  const [seguidoresLoaded, setSeguidoresLoaded] = useState(false);
  const [empresasLoaded, setEmpresasLoaded] = useState(false);
  const [ticketsLoaded, setTicketsLoaded] = useState(false);

  // Estados para las dos secciones de tickets
  const [ticketsProximos, setTicketsProximos] = useState([]);
  const [ticketsPasados, setTicketsPasados] = useState([]);
  const [loadingProximos, setLoadingProximos] = useState(false);
  const [loadingPasados, setLoadingPasados] = useState(false);

  // Estados para las tres secciones de links de referido
  const [referralLinksActivos, setReferralLinksActivos] = useState([]);
  const [referralLinksFinalizados, setReferralLinksFinalizados] = useState([]);
  const [referralLinksCancelados, setReferralLinksCancelados] = useState([]);
  const [loadingReferralLinks, setLoadingReferralLinks] = useState(false);

  // Estado para controlar qué tab está activa
  const [activeTab, setActiveTab] = useState(0);
  const [tabsDataLoaded, setTabsDataLoaded] = useState({
    tickets: false,
    empresas: false,
    eventos: false,
    informacion: false,
    invitaciones: false,
  });

  // Estados para invitaciones
  const [loadingInvitaciones, setLoadingInvitaciones] = useState(false);

  // Redux state
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const {
    info,
    isLoading: userLoading,
    error: userError,
  } = useSelector((state) => state.user);

  const { seguidoresInfo, seguidoresIsLoading, seguidoresError } = useSelector(
    (state) => state.seguidores,
  );

  const {
    invitacionesActivas,
    invitacionesActivasHasNextPage,
    invitacionesActivasCurrentPage,
    invitacionesRechazadas,
    invitacionesRechazadasHasNextPage,
    invitacionesRechazadasCurrentPage,
    invitacionesPasadas,
    invitacionesPasadasHasNextPage,
    invitacionesPasadasCurrentPage,
  } = useSelector((state) => state.invitacion);

  console.log("Rendering Perfil with info:", user);

  // Cargar información básica del usuario al montar
  useEffect(() => {
    const loadBasicData = async () => {
      // Prevenir ejecución duplicada
      if (hasLoadedBasicData.current) return;
      hasLoadedBasicData.current = true;

      setUserInfoLoaded(false);
      setSeguidoresLoaded(false);
      setEmpresasLoaded(false);
      setTicketsLoaded(false);

      try {
        await dispatch(getUserInfo({ uid: id }));
        setUserInfoLoaded(true);
      } catch (error) {
        console.error("Error loading user info:", error);
        setUserInfoLoaded(true);
      }

      try {
        await dispatch(getSeguidores());
        setSeguidoresLoaded(true);
      } catch (error) {
        console.error("Error loading seguidores:", error);
        setSeguidoresLoaded(true);
      }

      // Cargar ambas secciones de tickets en paralelo
      try {
        const now = new Date().toISOString();

        const [proximosResponse, pasadosResponse] = await Promise.all([
          // Tickets de eventos no finalizados (próximos o en curso)
          dispatch(
            getMisTicketsComprados({
              eventoFechaHoraFinDesde: now,
              sortBy: "eventoFechaHoraInicio",
              sortOrder: "asc",
              page: 1,
              limit: 10,
            }),
          ).unwrap(),
          // Tickets de eventos pasados (finalizados, sin filtro de vigencia)
          dispatch(
            getMisTicketsComprados({
              eventoFechaHoraFinHasta: now,
              sortBy: "eventoFechaHoraFin",
              sortOrder: "desc",
              page: 1,
              limit: 10,
            }),
          ).unwrap(),
        ]);

        console.log("🟢 Tickets Próximos Response:", proximosResponse);
        console.log("🟢 Tickets Pasados Response:", pasadosResponse);

        // Procesar tickets próximos
        const ticketsProximosFormateados = formatTickets(
          proximosResponse.tickets || [],
        );
        setTicketsProximos(ticketsProximosFormateados);
        console.log(
          "🟢 Tickets Próximos Formateados:",
          ticketsProximosFormateados.length,
        );

        // Procesar tickets pasados
        const ticketsPasadosFormateados = formatTickets(
          pasadosResponse.tickets || [],
        );
        setTicketsPasados(ticketsPasadosFormateados);
        console.log(
          "🟢 Tickets Pasados Formateados:",
          ticketsPasadosFormateados.length,
        );

        setTicketsLoaded(true);
        setTabsDataLoaded((prev) => ({ ...prev, tickets: true }));
      } catch (error) {
        console.error("Error loading tickets:", error);
        setTicketsLoaded(true);
      }

      // Cargar empresas para determinar si mostrar la tab
      try {
        await dispatch(getMisEmpresas());
        setEmpresasLoaded(true);
        setTabsDataLoaded((prev) => ({ ...prev, empresas: true }));
      } catch (error) {
        console.error("Error loading empresas:", error);
        setEmpresasLoaded(true);
      }
    };

    loadBasicData();
  }, [dispatch, id]);

  // Función para cargar tickets (ya se cargaron en loadBasicData)
  const loadTicketsData = async () => {
    // Prevenir ejecución duplicada
    if (hasLoadedTicketsData.current) return;

    // Los tickets ya se cargaron en el useEffect inicial
    // Esta función solo sirve para mantener consistencia en handleTabChange
    hasLoadedTicketsData.current = true;
    if (!tabsDataLoaded.tickets) {
      setTabsDataLoaded((prev) => ({ ...prev, tickets: true }));
    }
  };

  // Función para cargar empresas (ya se cargaron en loadBasicData)
  const loadEmpresasData = async () => {
    // Prevenir ejecución duplicada
    if (hasLoadedEmpresasData.current) return;

    // Las empresas ya se cargaron en el useEffect inicial
    // Esta función solo sirve para mantener consistencia en handleTabChange
    hasLoadedEmpresasData.current = true;
    if (!tabsDataLoaded.empresas) {
      setTabsDataLoaded((prev) => ({ ...prev, empresas: true }));
    }
  };

  // Función para cargar eventos (links de referido)
  const loadEventosData = async () => {
    // Prevenir ejecución duplicada
    if (hasLoadedEventosData.current) return;
    if (tabsDataLoaded.eventos) return;

    hasLoadedEventosData.current = true;
    setLoadingReferralLinks(true);

    try {
      const now = new Date().toISOString();

      const [activosResponse, finalizadosResponse, canceladosResponse] =
        await Promise.all([
          // Links de eventos activos (próximos a comenzar)
          dispatch(
            getMyReferralLinks({
              eventoEstado: "activo",
              eventoFechaHoraInicioDesde: now,
              sortBy: "eventoFechaHoraInicio",
              sortOrder: "asc",
              page: 1,
              limit: 10,
            }),
          ).unwrap(),
          // Links de eventos finalizados
          dispatch(
            getMyReferralLinks({
              eventoEstado: "activo",
              eventoFechaHoraFinHasta: now,
              sortBy: "eventoFechaHoraFin",
              sortOrder: "desc",
              page: 1,
              limit: 10,
            }),
          ).unwrap(),
          // Links de eventos cancelados
          dispatch(
            getMyReferralLinks({
              eventoEstado: "cancelado",
              sortBy: "eventoFechaHoraInicio",
              sortOrder: "desc",
              page: 1,
              limit: 10,
            }),
          ).unwrap(),
        ]);

      console.log("🔗 Referral Links Activos:", activosResponse);
      console.log("🔗 Referral Links Finalizados:", finalizadosResponse);
      console.log("🔗 Referral Links Cancelados:", canceladosResponse);

      setReferralLinksActivos(activosResponse.links || []);
      setReferralLinksFinalizados(finalizadosResponse.links || []);
      setReferralLinksCancelados(canceladosResponse.links || []);

      setTabsDataLoaded((prev) => ({ ...prev, eventos: true }));
    } catch (error) {
      console.error("Error loading referral links:", error);
      setTabsDataLoaded((prev) => ({ ...prev, eventos: true }));
    } finally {
      setLoadingReferralLinks(false);
    }
  };

  // Función para cargar invitaciones del usuario
  const loadInvitacionesData = async () => {
    if (hasLoadedInvitacionesData.current) return;
    if (tabsDataLoaded.invitaciones) return;

    hasLoadedInvitacionesData.current = true;
    setLoadingInvitaciones(true);

    try {
      await Promise.all([
        dispatch(
          getInvitacionesDeUsuario({ tipo: "activas", page: 1, limit: 10 }),
        ),
        dispatch(
          getInvitacionesDeUsuario({ tipo: "rechazadas", page: 1, limit: 10 }),
        ),
        dispatch(
          getInvitacionesDeUsuario({ tipo: "pasadas", page: 1, limit: 10 }),
        ),
      ]);
      setTabsDataLoaded((prev) => ({ ...prev, invitaciones: true }));
    } catch (error) {
      console.error("Error loading invitaciones:", error);
      setTabsDataLoaded((prev) => ({ ...prev, invitaciones: true }));
    } finally {
      setLoadingInvitaciones(false);
    }
  };

  // Función para cargar información (placeholder)
  const loadInformacionData = async () => {
    if (tabsDataLoaded.informacion) return;

    // TODO: Implementar carga de información cuando esté disponible
    console.log("Loading información data...");
    setTabsDataLoaded((prev) => ({ ...prev, informacion: true }));
  };

  // Manejar cambio de tab
  const handleTabChange = (index) => {
    setActiveTab(index);

    // Cargar datos según la tab seleccionada
    const tabName = tabs[index]?.name.toLowerCase();

    switch (tabName) {
      case "tickets":
        loadTicketsData();
        break;
      case "invitaciones":
        loadInvitacionesData();
        break;
      case "empresas":
        loadEmpresasData();
        break;
      case "eventos":
        loadEventosData();
        break;
      case "información":
        loadInformacionData();
        break;
    }
  };

  // Estados de carga individuales para cada sección
  const isLoadingPortada = !userInfoLoaded || userLoading;
  const isLoadingInfoPerfil = !userInfoLoaded || userLoading;
  const isLoadingOptions =
    !userInfoLoaded || !seguidoresLoaded || userLoading || seguidoresIsLoading;
  const isLoadingTickets = !ticketsLoaded;
  const isLoadingEmpresas = !empresasLoaded;

  // Estado de carga para determinar si mostrar/ocultar tabs
  const isLoadingBasicInfo = !userInfoLoaded || !empresasLoaded;

  // const DetailsPerfil = {
  //   statsList: [
  //     {
  //       colorName: "gray-200",
  //       stats: [
  //         {
  //           count: "9.542",
  //           label: "Visitas",
  //         },
  //       ],
  //     },
  //     {
  //       statName: "Tipo Perfil",
  //       icon: null,
  //       colorName: "gray-200",
  //       stats: [
  //         {
  //           count: "368",
  //           label: "Vendidos",
  //         },
  //         {
  //           count: "50",
  //           label: "Invitaciones",
  //         },
  //       ],
  //     },
  //     {
  //       statName: "Destacado",
  //       icon: "medal",
  //       colorName: "amber-400",
  //       stats: [
  //         {
  //           count: "11.558",
  //           label: "Seguidores",
  //         },
  //         {
  //           count: "2.558",
  //           label: "Seguidos",
  //         },
  //       ],
  //     },
  //   ],
  // };

  // console.log("Información del perfil:", info);

  // Función helper para formatear tickets
  const formatTickets = (ticketsComprados) => {
    return ticketsComprados.flatMap((grupo) =>
      grupo.tickets.map((ticket) => ({
        id: ticket.id,
        uid: ticket.uid,
        fechaHoraInicio: grupo.evento.fechaHoraInicio,
        zonaHoraria: grupo.evento.zonaHoraria,
        dateTime: formatFechaHoraEvento(new Date(grupo.evento.fechaHoraInicio)),
        title: grupo.evento.nombre,
        discount: ticket.precioDescuento > 0,
        direccion: ticket.direccion,
        image:
          ticket.tipoTicketImg != null
            ? ticket.tipoTicketImg
            : grupo.evento.img,
        vigente: ticket.vigente,
        qr: ticket.qr,
        numeroTributario: ticket.numeroTributario,
        fechaHoraExpiracion: ticket.fechaHoraExpiracion,
        fechaHoraCompra: ticket.fechaHoraCompra,
        precioVenta: ticket.precioVenta,
        precioDescuento: ticket.precioDescuento,
        precioPagado: ticket.precioPagado,
        tipoTicket: ticket.tipoTicket,
        eventoUid: grupo.evento.uid,
        usuarioUid: user.uid,
      })),
    );
  };

  // Obtener tickets comprados del estado de Redux (mantener para compatibilidad)
  const ticketsComprados = info?.ticketsComprados?.tickets || [];
  const totalTickets = info?.ticketsComprados?.total || 0;
  const ticketsVigentes = info?.ticketsComprados?.vigentes || 0;
  const ticketsExpirados = info?.ticketsComprados?.expirados || 0;

  // Convertir formato de tickets para el componente
  const ticketsFormateados = ticketsComprados.flatMap((grupo) =>
    grupo.tickets.map((ticket) => ({
      id: ticket.id,
      uid: ticket.uid,
      dateTime: formatFechaHoraEvento(new Date(grupo.evento.fechaHoraInicio)),
      title: grupo.evento.nombre,
      discount: ticket.precioDescuento > 0,
      direccion: ticket.direccion,
      image:
        ticket.tipoTicketImg != null ? ticket.tipoTicketImg : grupo.evento.img,
      vigente: ticket.vigente,
      qr: ticket.qr,
      numeroTributario: ticket.numeroTributario,
      fechaHoraExpiracion: ticket.fechaHoraExpiracion,
      fechaHoraCompra: ticket.fechaHoraCompra,
      precioVenta: ticket.precioVenta,
      precioDescuento: ticket.precioDescuento,
      precioPagado: ticket.precioPagado,
      tipoTicket: ticket.tipoTicket,
      eventoUid: grupo.evento.uid,
      usuarioUid: user.uid,
    })),
  );

  // Menu options
  let menuOptions = [
    {
      type: "none",
      classContent: "bg-aw-bluedark p-1.5",
    },
  ];

  console.log("info?.ambassadorActive:", id);
  switch (info?.ambassadorActive) {
    case 0: // Aún no envía su solicitud para ser embajador
      menuOptions.push({
        type: "link",
        label: "Activarme como Embajador",
        icon: "fa-solid fa-user-tie",
        to: `/perfil/${id}/activar-embajador`,
        className: "text-white hover:bg-aw-green/20",
        // Esta opción debe construirse dinámicamente en el componente para incluir el UID correcto
      });
      break;
    case 2: // Solicitud en revisión
      menuOptions.push({
        type: "button",
        label: "Solicitud de Embajador en Revisión",
        icon: "fa-solid fa-hourglass-half",
        onClick: () => alert("Tu solicitud de embajador está en revisión."),
        className: "text-white bg-yellow-500/60 animate-pulse",
      });
      break;
    case 3: // Solicitud rechazada
      menuOptions.push({
        type: "button",
        label: "Solicitud de Embajador Rechazada",
        icon: "fa-solid fa-circle-xmark",
        onClick: () =>
          alert(
            "Tu solicitud de embajador ha sido rechazada. Por favor, contacta al soporte para más información.",
          ),
        className: "text-white bg-red-500/60",
      });
      break;
    default:
      // Ya es embajador o estado desconocido, no mostrar opción
      break;
  }

  console.log("[info]:", info);

  // Función para compartir el perfil del usuario
  const handleSharePerfil = async () => {
    const perfilUrl = `/usuario/${user?.uid}`;

    await shareContent({
      url: perfilUrl,
      title:
        `${info?.nombres || ""} ${info?.apellidos || ""}`.trim() ||
        "Perfil de Usuario",
      text: `¡Conoce a ${info?.nombres || "este usuario"}!`,
      successMessage: "¡Link del perfil copiado al portapapeles!",
    });
  };

  menuOptions.push(
    {
      type: "link",
      label: "Crear Empresa",
      icon: "fa-solid fa-building",
      to: "/empresa/crear",
      className: "text-white hover:bg-aw-green/20",
    },
    { divider: true },
    {
      type: "button",
      label: "Compartir Perfil",
      icon: "fa-solid fa-share",
      onClick: handleSharePerfil,
      className: "text-white hover:bg-aw-green/20",
    },
    // {
    //   type: "button",
    //   label: "Copiar Enlace",
    //   icon: "fa-solid fa-link",
    //   onClick: () => {
    //     navigator.clipboard.writeText(window.location.href);
    //     alert("Enlace copiado");
    //   },
    //   className: "text-white hover:bg-aw-green/20",
    // }
  );
  // TODO: Habilitar opción de reportar perfil en el futuro
  // { divider: true },
  // {
  //   type: "button",
  //   label: "Reportar",
  //   icon: "fa-solid fa-flag",
  //   onClick: () => console.log("Reportar"),
  //   className: "text-red-500 hover:bg-aw-green/20"
  // }

  // Construir tabs dinámicamente - Tickets e Información siempre primero
  const tabs = !isLoadingBasicInfo
    ? [
        // 1. Tickets - Siempre presente al inicio
        {
          name: "Mis Tickets",
          icon: "fa-solid fa-ticket",
          current: true,
        },
        // 2. Invitaciones - Siempre presente
        {
          name: "Invitaciones",
          icon: "fa-solid fa-envelope-open-text",
          current: false,
        },
        // 3. Información - Siempre presente en segundo lugar
        {
          name: "Información",
          icon: "fa-solid fa-circle-info",
          current: false,
        },
        // 4. Empresas - Condicional (solo si tiene al menos una)
        ...(info?.empresas && info.empresas.length > 0
          ? [
              {
                name: "Empresas",
                icon: "fa-solid fa-building",
                current: false,
              },
            ]
          : []),
        // 5. Eventos - Condicional (solo si es embajador activo)
        ...(info?.ambassadorActive === 1
          ? [
              {
                name: "Eventos",
                icon: "fa-solid fa-calendar-days",
                current: false,
              },
            ]
          : []),
      ]
    : [];

  const tabInfo = {
    title: "",
    description: "",
    layout: "horizontal",
    textColor: "text-gray-200",
    textColorHover: "hover:text-white",
    textColorActive: "text-aw-green",
    barTabActive: "bg-aw-green",
    bgColor: "bg-aw-bluedark",
    bgColorHover: "hover:bg-aw-bluedark-75",
    bgColorActive: "bg-aw-blueblack-95",
    borderColor: "border-gray-700",
    borderColorActive: "aw-green",
    tabs: tabs,
  };

  return (
    <>
      <div className="w-full max-w-5xl mx-auto relative">
        {/* Portada */}
        {isLoadingPortada ? (
          <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
            <Skeleton height={320} className="w-full rounded-b-3xl" />
          </SkeletonTheme>
        ) : (
          <Portada info={info} />
        )}
      </div>
      <div
        className={`w-full rounded-t-[8%] bg-aw-blueblack text-aw flex flex-col items-center pt-20 -mt-20 sm:-mt-[5.5rem] ${
          info?.empresas?.length > 0 ? "" : "min-h-screen"
        }`}
      >
        {/* Content Perfil */}
        <div className="w-full max-w-5xl mx-auto relative sm:mb-8">
          <div className="flex flex-col sm:flex-row items-end sm:items-center gap-4 px-2 z-10 relative">
            {/* Información del Perfil */}
            {isLoadingInfoPerfil ? (
              <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
                <div className="w-full sm:w-1/2 flex flex-row items-end sm:items-center gap-4 px-2 z-10 relative">
                  <div className="relative">
                    <Skeleton
                      circle={true}
                      height={112}
                      width={112}
                      className="rounded-full border-4 border-white object-cover shadow-lg bg-aw-bluegray"
                    />
                  </div>
                  <div className="flex flex-col gap-2 mb-6">
                    <Skeleton height={24} width={200} borderRadius={8} />
                    <Skeleton height={20} width={150} borderRadius={8} />
                  </div>
                </div>
              </SkeletonTheme>
            ) : (
              <InfoPerfil info={info} />
            )}

            {/* Opciones en Perfil */}
            {isLoadingOptions ? (
              <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
                <div className="flex w-full sm:w-1/2 mt-8 justify-center sm:items-center">
                  <div className="flex flex-col items-center w-full sm:items-end gap-2">
                    <div className="flex flex-col items-center w-full sm:items-end gap-2">
                      <div className="flex items-center -space-x-4 mb-1">
                        <Skeleton circle={true} height={40} width={40} />
                        <Skeleton circle={true} height={40} width={40} />
                        <Skeleton circle={true} height={40} width={40} />
                        <Skeleton circle={true} height={40} width={40} />
                        <span className="ml-6 text-gray-200 text-sm font-semibold">
                          <Skeleton height={16} width={80} />
                        </span>
                      </div>
                    </div>
                    <div className="flex w-full sm:w-1/2 justify-center sm:items-center">
                      <div className="flex flex-row justify-between gap-x-5 sm:gap-x-2">
                        <Skeleton height={40} width={120} borderRadius={8} />
                        <Skeleton height={40} width={40} borderRadius={8} />
                        <Skeleton height={40} width={40} borderRadius={8} />
                      </div>
                    </div>
                  </div>
                </div>
              </SkeletonTheme>
            ) : (
              <OptionsPerfil
                info={info}
                people={seguidoresInfo}
                menuOptions={menuOptions}
              />
            )}
          </div>
        </div>

        {/* Skeleton para Tabs mientras carga */}
        {isLoadingBasicInfo && (
          <div className="w-full px-4">
            <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
              <div className="flex w-full gap-2">
                <Skeleton height={44} width={300} borderRadius={8} />
              </div>
            </SkeletonTheme>
          </div>
        )}

        {/* Tabs Component */}
        <div className="w-full pb-8">
          <Tab tabInfo={tabInfo} onTabChange={handleTabChange}>
            {/* Tab 1: Tickets - Siempre presente */}
            <div className="bg-aw-bluedark-95 rounded-lg py-4 min-h-[400px]">
              {isLoadingTickets ? (
                <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
                  <div className="flex gap-4 overflow-x-auto scrollbar-hide">
                    {[1, 2, 3, 4, 5].map((index) => (
                      <div key={index} className="min-w-[200px] max-w-[200px]">
                        <Skeleton
                          height={208}
                          className="mb-2"
                          borderRadius={8}
                        />
                        <div className="px-2 py-1">
                          <Skeleton height={20} className="mb-1" />
                          <Skeleton height={14} />
                        </div>
                        <Skeleton height={36} className="mt-2" />
                      </div>
                    ))}
                  </div>
                </SkeletonTheme>
              ) : (
                <>
                  {/* Sección 1: Próximos Eventos */}
                  <div className="mb-8">
                    <h3 className="text-xl font-semibold mb-4 pl-4 text-white">
                      Mis Tickets
                    </h3>
                    {ticketsProximos.length > 0 ? (
                      <TicketCarousel
                        tickets={ticketsProximos}
                        TicketItem={TicketCardPerfilUsuario}
                        sectionType="ticketsProximos"
                        uid={user.uid}
                        onLoadMore={(newTickets) => {
                          console.log(
                            "📥 onLoadMore Próximos - Tickets recibidos:",
                            newTickets,
                          );
                          const ticketsFormateados = formatTickets(newTickets);
                          console.log(
                            "✅ Tickets Próximos Formateados:",
                            ticketsFormateados.length,
                          );
                          setTicketsProximos((prev) => [
                            ...prev,
                            ...ticketsFormateados,
                          ]);
                        }}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                        <i className="fas fa-calendar-check text-5xl mb-4 opacity-30" />
                        <h4 className="text-lg font-semibold mb-2 text-white">
                          No tienes próximos eventos
                        </h4>
                        <p className="text-center text-sm mb-4">
                          Explora eventos disponibles y asegura tu lugar
                        </p>
                        <Link
                          to="/"
                          className="px-6 py-2 bg-aw-green text-aw-bluedark rounded-lg font-semibold hover:scale-105 transition-transform"
                          onClick={scrollToTop}
                        >
                          Explorar Eventos
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Sección 2: Eventos Pasados */}
                  <div className="mb-8">
                    <h3 className="text-xl font-semibold mb-4 pl-4 text-white">
                      Mis Tickets Pasados
                    </h3>
                    {ticketsPasados.length > 0 ? (
                      <TicketCarousel
                        tickets={ticketsPasados}
                        TicketItem={TicketCardPerfilUsuario}
                        sectionType="ticketsPasados"
                        uid={user.uid}
                        onLoadMore={(newTickets) => {
                          console.log(
                            "📥 onLoadMore Pasados - Tickets recibidos:",
                            newTickets,
                          );
                          const ticketsFormateados = formatTickets(newTickets);
                          console.log(
                            "✅ Tickets Pasados Formateados:",
                            ticketsFormateados.length,
                          );
                          setTicketsPasados((prev) => [
                            ...prev,
                            ...ticketsFormateados,
                          ]);
                        }}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                        <i className="fas fa-calendar-xmark text-5xl mb-4 opacity-30" />
                        <h4 className="text-lg font-semibold mb-2 text-white">
                          No tienes eventos pasados
                        </h4>
                        <p className="text-center text-sm">
                          Aquí aparecerán los tickets de eventos finalizados
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Tab 2: Invitaciones - Siempre presente */}
            {!isLoadingBasicInfo && (
              <div className="bg-aw-bluedark-95 rounded-lg py-4 min-h-[400px]">
                {loadingInvitaciones ? (
                  <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
                    <div className="flex gap-4 overflow-x-auto scrollbar-hide">
                      {[1, 2, 3, 4, 5].map((index) => (
                        <div
                          key={index}
                          className="min-w-[200px] max-w-[200px]"
                        >
                          <Skeleton
                            height={208}
                            className="mb-2"
                            borderRadius={8}
                          />
                          <div className="px-2 py-1">
                            <Skeleton height={20} className="mb-1" />
                            <Skeleton height={14} />
                          </div>
                          <Skeleton height={36} className="mt-2" />
                        </div>
                      ))}
                    </div>
                  </SkeletonTheme>
                ) : (
                  <>
                    {/* Activas */}
                    <div className="mb-8">
                      <h3 className="text-xl font-semibold mb-4 pl-4 text-white">
                        Mis Invitaciones Activas
                      </h3>
                      {invitacionesActivas.length > 0 ? (
                        <TicketCarousel
                          tickets={invitacionesActivas}
                          TicketItem={TicketCardInvitacion}
                          sectionType="invitacionesActivas"
                          uid={user.uid}
                          onLoadMore={() => {
                            if (invitacionesActivasHasNextPage) {
                              dispatch(
                                getInvitacionesDeUsuario({
                                  tipo: "activas",
                                  page: invitacionesActivasCurrentPage + 1,
                                  limit: 10,
                                }),
                              );
                            }
                          }}
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                          <i className="fas fa-envelope-open-text text-5xl mb-4 opacity-30" />
                          <h4 className="text-lg font-semibold mb-2 text-white">
                            No tienes invitaciones activas
                          </h4>
                          <p className="text-center text-sm">
                            Solicita invitaciones en los eventos que te
                            interesen
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Pasadas */}
                    <div className="mb-8">
                      <h3 className="text-xl font-semibold mb-4 pl-4 text-white">
                        Mis Invitaciones Pasadas
                      </h3>
                      {invitacionesPasadas.length > 0 ? (
                        <TicketCarousel
                          tickets={invitacionesPasadas}
                          TicketItem={TicketCardInvitacion}
                          sectionType="invitacionesPasadas"
                          uid={user.uid}
                          onLoadMore={() => {
                            if (invitacionesPasadasHasNextPage) {
                              dispatch(
                                getInvitacionesDeUsuario({
                                  tipo: "pasadas",
                                  page: invitacionesPasadasCurrentPage + 1,
                                  limit: 10,
                                }),
                              );
                            }
                          }}
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                          <i className="fas fa-calendar-check text-5xl mb-4 opacity-30" />
                          <h4 className="text-lg font-semibold mb-2 text-white">
                            No tienes invitaciones pasadas
                          </h4>
                          <p className="text-center text-sm">
                            Aqui apareceran las invitaciones de eventos
                            finalizados
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Rechazadas */}
                    <div className="mb-8">
                      <h3 className="text-xl font-semibold mb-4 pl-4 text-white">
                        Mis Invitaciones Rechazadas
                      </h3>
                      {invitacionesRechazadas.length > 0 ? (
                        <TicketCarousel
                          tickets={invitacionesRechazadas}
                          TicketItem={TicketCardInvitacion}
                          sectionType="invitacionesRechazadas"
                          uid={user.uid}
                          onLoadMore={() => {
                            if (invitacionesRechazadasHasNextPage) {
                              dispatch(
                                getInvitacionesDeUsuario({
                                  tipo: "rechazadas",
                                  page: invitacionesRechazadasCurrentPage + 1,
                                  limit: 10,
                                }),
                              );
                            }
                          }}
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                          <i className="fas fa-circle-xmark text-5xl mb-4 opacity-30" />
                          <h4 className="text-lg font-semibold mb-2 text-white">
                            No tienes invitaciones rechazadas
                          </h4>
                          <p className="text-center text-sm">
                            Aqui apareceran las solicitudes que fueron
                            rechazadas
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Tab 3: Información - Siempre presente */}
            <EditarInformacionPerfil />

            {/* Tab 4: Empresas - Condicional (solo si tiene empresas) */}
            {!isLoadingBasicInfo &&
              info?.empresas &&
              info.empresas.length > 0 && (
                <div className="bg-aw-bluedark-95 rounded-lg p-4 min-h-[400px]">
                  {isLoadingEmpresas ? (
                    <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[1, 2, 3].map((index) => (
                          <div key={index}>
                            <Skeleton height={150} borderRadius={8} />
                          </div>
                        ))}
                      </div>
                    </SkeletonTheme>
                  ) : (
                    <>
                      <h3 className="text-xl font-semibold mb-4 text-white">
                        Mis Empresas ({info?.empresas?.length || 0})
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {info?.empresas?.map((empresa, index) => (
                          <CardMiEmpresa
                            key={index}
                            empresa={empresa}
                            scrollToTop={scrollToTop}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

            {/* Tab 5: Eventos - Condicional (solo si es embajador activo) */}
            {!isLoadingBasicInfo && info?.ambassadorActive === 1 && (
              <div className="bg-aw-bluedark-95 rounded-lg py-4 min-h-[400px]">
                {loadingReferralLinks ? (
                  <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
                    <div className="flex gap-4 overflow-x-auto scrollbar-hide">
                      {[1, 2, 3, 4, 5].map((index) => (
                        <div
                          key={index}
                          className="min-w-[200px] max-w-[200px]"
                        >
                          <Skeleton
                            height={208}
                            className="mb-2"
                            borderRadius={8}
                          />
                          <div className="px-2 py-1">
                            <Skeleton height={20} className="mb-1" />
                            <Skeleton height={14} />
                          </div>
                          <Skeleton height={36} className="mt-2" />
                        </div>
                      ))}
                    </div>
                  </SkeletonTheme>
                ) : (
                  <>
                    {/* Sección 1: Eventos Activos */}
                    <div className="mb-8">
                      <h3 className="text-xl font-semibold mb-4 pl-4 text-white">
                        Mis Links de Eventos Activos
                      </h3>
                      {referralLinksActivos.length > 0 ? (
                        <TicketCarousel
                          tickets={referralLinksActivos}
                          TicketItem={TicketCardReferralLink}
                          sectionType="referralLinksActivos"
                          uid={user.uid}
                          onLoadMore={(newLinks) => {
                            console.log(
                              "📥 onLoadMore Activos - Links recibidos:",
                              newLinks,
                            );
                            setReferralLinksActivos((prev) => [
                              ...prev,
                              ...newLinks,
                            ]);
                          }}
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                          <i className="fas fa-link text-5xl mb-4 opacity-30" />
                          <h4 className="text-lg font-semibold mb-2 text-white">
                            No tienes links de eventos activos
                          </h4>
                          <p className="text-center text-sm mb-4">
                            Genera links de referido para eventos próximos
                          </p>
                          <Link
                            to="/"
                            className="px-6 py-2 bg-aw-green text-aw-bluedark rounded-lg font-semibold hover:scale-105 transition-transform"
                            onClick={scrollToTop}
                          >
                            Explorar Eventos
                          </Link>
                        </div>
                      )}
                    </div>

                    {/* Sección 2: Eventos Finalizados */}
                    <div className="mb-8">
                      <h3 className="text-xl font-semibold mb-4 pl-4 text-white">
                        Mis Links de Eventos Finalizados
                      </h3>
                      {referralLinksFinalizados.length > 0 ? (
                        <TicketCarousel
                          tickets={referralLinksFinalizados}
                          TicketItem={TicketCardReferralLink}
                          sectionType="referralLinksFinalizados"
                          uid={user.uid}
                          onLoadMore={(newLinks) => {
                            console.log(
                              "📥 onLoadMore Finalizados - Links recibidos:",
                              newLinks,
                            );
                            setReferralLinksFinalizados((prev) => [
                              ...prev,
                              ...newLinks,
                            ]);
                          }}
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                          <i className="fas fa-calendar-check text-5xl mb-4 opacity-30" />
                          <h4 className="text-lg font-semibold mb-2 text-white">
                            No tienes links de eventos finalizados
                          </h4>
                          <p className="text-center text-sm">
                            Aquí aparecerán los links de eventos que ya
                            finalizaron
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Sección 3: Eventos Cancelados */}
                    <div className="mb-8">
                      <h3 className="text-xl font-semibold mb-4 pl-4 text-white">
                        Mis Links de Eventos Cancelados
                      </h3>
                      {referralLinksCancelados.length > 0 ? (
                        <TicketCarousel
                          tickets={referralLinksCancelados}
                          TicketItem={TicketCardReferralLink}
                          sectionType="referralLinksCancelados"
                          uid={user.uid}
                          onLoadMore={(newLinks) => {
                            console.log(
                              "📥 onLoadMore Cancelados - Links recibidos:",
                              newLinks,
                            );
                            setReferralLinksCancelados((prev) => [
                              ...prev,
                              ...newLinks,
                            ]);
                          }}
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                          <i className="fas fa-calendar-xmark text-5xl mb-4 opacity-30" />
                          <h4 className="text-lg font-semibold mb-2 text-white">
                            No tienes links de eventos cancelados
                          </h4>
                          <p className="text-center text-sm">
                            Aquí aparecerán los links de eventos cancelados
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </Tab>
        </div>
      </div>
    </>
  );
}
